import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskedLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SERVICES } from "@/data/services";
import { usePageMeta } from "@/utils/seo";

export default function Leistungen() {
  const location = useLocation();
  usePageMeta({
    title: "Leistungen | Gebäudeunterhalt, Reinigung, Winterservice & mehr | HRG Service GmbH",
    description: "Alle Leistungen der HRG Service GmbH: Gebäudeunterhalt, Unterhaltsreinigung, Winterservice, Spezialreinigung, Gartenarbeiten und Entsorgung in Aargau, Luzern, Zürich und Zug.",
    path: "/leistungen",
  });

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  return (
    <main className="pt-20">
      <section className="bg-mesh border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
          <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#3B62A5] font-semibold mb-6 flex items-center gap-3">
            <span className="inline-block h-[2px] w-10 bg-[#3B62A5]" /> Leistungen
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A111E] leading-[1.08]">
            <MaskedLines lines={[<>Alles rund ums Gebäude —</>, <span className="text-[#3B62A5]">aus einer Hand.</span>]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Sechs Fachbereiche, ein eingespieltes Team. Wählen Sie unten Ihre Dienstleistung und fordern Sie eine unverbindliche Offerte an.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 py-20 lg:py-28 space-y-24">
        {SERVICES.map((s, i) => (
          <div key={s.id} id={s.id} className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="glass-card group relative overflow-hidden rounded-3xl">
                <img src={s.img} alt={s.alt} loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <span className="absolute left-5 top-5 rounded-full bg-[#3B62A5] px-3 py-1.5 font-tech text-xs font-semibold text-white">{s.nr}</span>
              </div>
            </Reveal>
            <Reveal delay={0.12} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#3B62A5] font-semibold">{s.nr} — Fachbereich</p>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A111E]">{s.title}</h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">{s.intro}</p>
              <ul className="mt-6 space-y-3">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-[#E8F0FE]"><Check className="h-3.5 w-3.5 text-[#3B62A5]" /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <Link
                  to={`/leistungen/${s.slug}`}
                  data-testid={`leistungen-detail-${s.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#3B62A5] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#2A477A]"
                >
                  Mehr erfahren
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
                <Link
                  to={`/kontakt?service=${s.slug}`}
                  data-testid={`leistungen-cta-${s.slug}`}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-[#3B62A5] transition-colors duration-200 hover:text-[#2A477A]"
                >
                  Offerte anfragen
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        ))}
      </section>

    </main>
  );
}
